/**
 * EditPropertyModal Component
 *
 * Modal for editing existing property details.
 *
 * ⚠️ WHAT THIS BOX WRITES IS NOT WHAT THE LETTER PRINTS (the audit of 9 Oct 2026, row 20).
 * It writes the one-line address, the town and the postcode. The letter is printed from a
 * home's Land Registry record where it has one, else from its address in parts (an import's
 * home), and only as a last resort from the one line. So for most homes an edited address
 * changed the page and NOT the letter, and nothing said so. Until the box writes what the
 * letter prints (its own reviewed change), it SHOWS the letter as it would print after the
 * save, and says plainly, before Save, when the letter will not change
 * (utils/letterPreview.js, which asks the letter's own function).
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FaTimes, FaSave, FaSpinner } from "react-icons/fa";
import { db, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import { useToast } from "./Toast";
import { PropertyRepository } from "../../models";
import { generatePropertyHash } from "../../utils";
import { editEffect } from "../../utils/letterPreview";
import InfoNotice from "./InfoNotice";
import "./AddPropertyModal.scss"; // Reuse the same styles

export const EditPropertyModal = ({ isOpen, onClose, onSuccess, property }) => {
  const toast = useToast();
  const propertyRepo = new PropertyRepository(db, functions); // Use repository directly
  const [updating, setUpdating] = useState(false);
  const [formData, setFormData] = useState({
    address: "",
    postcode: "",
    town: "",
    price: "",
    propertyStatus: "For Sale",
  });

  // Populate form when property prop changes
  useEffect(() => {
    if (property) {
      setFormData({
        address: property.address || "",
        postcode: property.postcode || "",
        town: property.town || "",
        price: property.price
          ? property.price.toString().replace(/[£,]/g, "")
          : "",
        propertyStatus: property.propertyStatus || "For Sale",
      });
    }
  }, [property]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.address.trim()) {
      toast.warning("Please enter an address");
      return;
    }

    if (!formData.postcode.trim()) {
      toast.warning("Please enter a postcode");
      return;
    }

    try {
      setUpdating(true);

      // Parse price if provided
      let parsedPrice = null;
      if (formData.price.trim()) {
        const priceStr = formData.price.replace(/[£,]/g, "").trim();
        if (!isNaN(priceStr) && priceStr !== "") {
          parsedPrice = parseInt(priceStr, 10);
        }
      }

      // Generate address hash
      const addressHash = generatePropertyHash({
        address: formData.address,
        postcode: formData.postcode,
      });

      // Generate normalized address for matching
      const addressNormalized = `${formData.address} ${formData.postcode}`
        .toLowerCase()
        .replace(/[^\w\s]/g, "")
        .replace(/\b(flat|apartment|apt|unit)\s*\d+\w?\b/gi, "")
        .replace(/\s+/g, " ")
        .trim();

      // Prepare updates
      const updates = {
        address: formData.address.trim(),
        postcode: formData.postcode.trim(),
        town: formData.town.trim() || null,
        price: parsedPrice,
        propertyStatus: formData.propertyStatus,
        addressHash,
        addressNormalized,
      };

      // Check if address or postcode changed - if so, re-geocode
      const addressChanged =
        formData.address.trim() !== (property.address || "").trim() ||
        formData.postcode.trim() !== (property.postcode || "").trim();

      // Update property using new architecture
      await propertyRepo.update(property.id, updates);

      // If address changed OR property doesn't have coordinates, geocode
      const needsGeocoding =
        addressChanged || !property.latitude || !property.longitude;

      if (needsGeocoding) {
        try {
          const geocodeFunction = httpsCallable(
            functions,
            "geocodePropertyAddress"
          );
          await geocodeFunction({
            propertyId: property.id,
            address: formData.address.trim(),
            postcode: formData.postcode.trim(),
          });

          if (addressChanged) {
            toast.success("Property updated and re-geocoded!");
          } else {
            toast.success("Property updated and geocoded!");
          }

          if (onSuccess) {
            // Hook will update property via real-time listener
            onSuccess();
          }
        } catch (geocodeError) {
          console.warn("Failed to geocode property:", geocodeError);
          toast.warning("Property updated but geocoding failed");

          if (onSuccess) {
            onSuccess({ ...property, ...updates });
          }
        }
      } else {
        toast.success("Property updated successfully!");

        if (onSuccess) {
          onSuccess({ ...property, ...updates });
        }
      }

      onClose();
    } catch (error) {
      console.error("Error updating property:", error);
      toast.error("Failed to update property. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  if (!isOpen) return null;

  // what the letter would carry if this form were saved as it stands, by the letter's own function
  const letter = editEffect(property, formData);

  return (
    <div className="add-property-modal-overlay" onClick={onClose}>
      <div className="add-property-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="add-property-modal__header">
            <div className="add-property-modal__title">
              <FaSave />
              <span>Edit Property</span>
            </div>
            <button
              type="button"
              className="add-property-modal__close"
              onClick={onClose}
            >
              <FaTimes />
            </button>
          </div>

          <div className="add-property-modal__body">
            <div className="form-group">
              <label htmlFor="address">
                Address <span className="required">*</span>
              </label>
              <input
                id="address"
                type="text"
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
                placeholder="e.g., 123 Main Street, London"
                required
                disabled={updating}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="postcode">
                  Postcode <span className="required">*</span>
                </label>
                <input
                  id="postcode"
                  type="text"
                  value={formData.postcode}
                  onChange={(e) => handleChange("postcode", e.target.value)}
                  placeholder="e.g., SW1A 1AA"
                  required
                  disabled={updating}
                />
              </div>

              <div className="form-group">
                <label htmlFor="town">Town</label>
                <input
                  id="town"
                  type="text"
                  value={formData.town}
                  onChange={(e) => handleChange("town", e.target.value)}
                  placeholder="e.g., London"
                  disabled={updating}
                />
              </div>
            </div>

            <div className="form-group" data-testid="edit-letter">
              <label>The mailshot, as it would print after saving</label>
              {letter.cannotPrint ? (
                <InfoNotice variant="error" data-testid="edit-letter-cannot-print">
                  A mailshot cannot be printed from this record as it is stored, and saving here will not put that right.
                </InfoNotice>
              ) : (
                <div
                  data-testid="edit-letter-lines"
                  style={{ fontFamily: "monospace", fontSize: "0.8125rem", lineHeight: 1.5, padding: "0.5rem 0.75rem", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "0.375rem" }}
                >
                  {letter.after.length > 0
                    ? letter.after.map((line, index) => <div key={`${index}-${line}`}>{line}</div>)
                    : <div>(nothing: the mailshot would carry no address)</div>}
                </div>
              )}
              {letter.standing && (
                <InfoNotice variant="warning" data-testid="edit-letter-standing" style={{ marginTop: "0.5rem" }}>
                  {letter.standing}
                </InfoNotice>
              )}
              {letter.warning && (
                <InfoNotice variant="error" data-testid="edit-letter-warning" style={{ marginTop: "0.5rem" }}>
                  <strong>{letter.warning}</strong>
                </InfoNotice>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="price">Price</label>
                <input
                  id="price"
                  type="text"
                  value={formData.price}
                  onChange={(e) => handleChange("price", e.target.value)}
                  placeholder="e.g., £250,000 or 250000"
                  disabled={updating}
                />
                <small className="help-text">
                  Enter as number (250000) or with £ and commas (£250,000)
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={formData.propertyStatus}
                  onChange={(e) =>
                    handleChange("propertyStatus", e.target.value)
                  }
                  disabled={updating}
                >
                  <option value="For Sale">For Sale</option>
                  <option value="Sold STC">Sold STC</option>
                  <option value="Under Offer">Under Offer</option>
                </select>
              </div>
            </div>
          </div>

          <div className="add-property-modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
              disabled={updating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={updating}
            >
              {updating ? (
                <>
                  <FaSpinner className="spinner" /> Updating...
                </>
              ) : (
                <>
                  <FaSave /> Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

EditPropertyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  property: PropTypes.shape({
    id: PropTypes.string.isRequired,
    address: PropTypes.string,
    postcode: PropTypes.string,
    town: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    propertyStatus: PropTypes.string,
  }).isRequired,
};

export default EditPropertyModal;
